import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import { useForm, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { z } from 'zod'
import { Filter, LoaderCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import queryString from 'query-string'
import store from '../../app/store'
import { COMPACT_BREAKPOINT } from '@/features/hooks/use-mobile'
import { Form } from '@/features/shadcn/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/features/shadcn/ui/button'
import Combobox from '@/features/forms/Combobox'
import {
  UnderlineTabs,
  UnderlineTabsList,
  UnderlineTabsTrigger,
  UnderlineTabsContent,
} from 'sharedComponents/UnderlineTabs'
import {
  fetchTrafficReport,
  fetchUniqueFieldValues,
  resetFetchGoogleAdsMetricsSummaryStatus,
  resetFetchFbAdsMetricsSummaryStatus,
  resetFetchKlaviyoAdsMetricsSummaryStatus,
  resetFetchPinterestAdsMetricsSummaryStatus,
  resetFetchTiktokAdsMetricsSummaryStatus,
  resetFetchTrafficMetricsStatus,
  resetFetchTrafficReportStatus,
  selectCurrentTab,
  selectMetricTableColumns,
  selectMetricTableRows,
  selectTabPagination,
  selectTrafficReport,
  selectTrafficReportStatus,
  setCurrentTab,
  setTabTotalPages,
  tabNameToReportKey,
} from './trafficReportSlice'
import { trafficSessionsConfig } from 'modules/formatting'
import { reportDefaultInvisibleColumns } from 'modules/reportConfigs'
import TrafficEditableThead from './components/TrafficEditableThead'
import {
  setSettingsModalOpen,
  initializeAttributionSettings,
} from './trafficSettingSlice'
import _ from 'lodash'
import { DEFAULT_TIME_PERIOD } from './constants'
import OverviewTab from './components/OverviewTab'
import MetricsTab from './components/MetricsTab'
import DataTableTab from './components/DataTableTab'
import { getFiltersFromQs } from './utils'
import { selectRootDomains, selectRootUser, selectRootUserDefaultCurrency, selectRootAdmin } from '@/rootSlice'
import { selectTrafficAddOnEnabled } from '../Plan/planSlice'
import { IoIosSave } from 'react-icons/io'
import { BsFunnelFill } from 'react-icons/bs'
import jsonToCsvExport from 'json-to-csv-export'
import AdsTab from './components/AdsTab'
import SettingsModal from './components/SettingsModal'
import MMMTab from './components/MMMTab'
import TrafficDateFilter from './components/TrafficDateFilter'
import TrafficFilterSideMenu, { FREE_TEXT_FILTER_FIELDS } from './components/TrafficFilterSideMenu'
import TrafficUpgradeModal from './components/TrafficUpgradeModal'
import { resolveVisibleSortedKeys, parseDefaultInvisibleSet } from './columnVisibility'
import { Sheet, SheetContent } from '@/features/shadcn/ui/sheet'

/**
 *
 * Global vars
 *
 */
export const timePeriodOptions = [
  { label: 'Last 5 minutes', value: '5-minutes' },
  { label: 'Last 10 minutes', value: '10-minutes' },
  { label: 'Last 30 minutes', value: '30-minutes' },
  { label: 'Last 60 minutes', value: '60-minutes' },
  { label: 'Last 24 hours', value: '24-hours' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: '7-days' },
  { label: 'Last 30 days', value: '30-days' },
  { label: 'Last 90 days', value: '90-days' },
  { label: 'Select Date Range', value: 'custom' },
]

// IMPORTANT: `value` should be the name of the traffic db field!
// JSONB-derived fields (sku, variant_id, product_name) translate to
// `storefront_data @> '{"lineItems":[{...}]}'` containment queries.
// Aggregate fields (cart_value, *_usd, etc.) are real columns with btree
// indexes — used with op:'range' for min/max filters.
export const propertyOptions = [
  { label: 'None', value: '' },
  { label: 'Cart Status', value: 'checkout_stage' },
  { label: 'Country', value: 'country_code' },
  { label: 'Currency', value: 'currency' },
  { label: 'Domain', value: 'domain' },
  { label: 'UTM Campaign', value: 'utm_campaign' },
  { label: 'UTM Medium', value: 'utm_medium' },
  { label: 'UTM Source', value: 'utm_source' },
  { label: 'UTM Term', value: 'utm_term' },
  // { label: 'UTM Content', value: 'utm_content' },
  { label: 'Traffic Type', value: 'traffic_type' },
  { label: 'Landing Page', value: 'entrypoint' },
  { label: 'SKU', value: 'sku' },
  { label: 'Variant ID', value: 'variant_id' },
  { label: 'Product Name', value: 'product_name' },
  // USD-converted aggregates: best for cross-currency comparisons.
  { label: 'Cart Value (USD)', value: 'cart_value_usd' },
  { label: 'Max Line Price (USD)', value: 'max_line_price_usd' },
  { label: 'Min Line Price (USD)', value: 'min_line_price_usd' },
  { label: 'Total Quantity', value: 'total_quantity' },
  // Native-currency siblings: pair with a Currency filter for clean comparison.
  { label: 'Cart Value (native)', value: 'cart_value' },
  { label: 'Max Line Price (native)', value: 'max_line_price' },
  { label: 'Min Line Price (native)', value: 'min_line_price' },
]

const cartStatusOptions = [
  { label: 'On Site', value: 'on_site' },
  { label: 'Cart Added', value: 'cart_added' },
  { label: 'Checking Out', value: 'checking_out' },
  { label: 'Order Complete', value: 'order_complete' },
]

function Traffic() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const timezone = useSelector(selectRootUser)?.default_timezone
  const currency = useSelector(selectRootUser)?.default_currency || 'USD'
  const domains = useSelector(selectRootDomains)
  const addOnEnabled = useSelector(selectTrafficAddOnEnabled)
  const addOnUpgradeDismissed = useSelector(state => !!state.plan.addOnUpgradeDismissed?.Traffic)
  const isAdmin = useSelector(selectRootAdmin)
  const isTrialing = useSelector(state => state.plan.subscription?.status === 'trialing')
  const qs = queryString.parse(location.search)

  /**
   * Initialize Traffic Settings
   */
  useEffect(() => {
    dispatch(initializeAttributionSettings())
  }, [])

  /**
   *
   * Time period form
   *
   */
  const FormSchema = z.object({
    timePeriod: z.string({
      required_error: 'Please select a time period.',
    }),
    customDateRange: z.object({
      start: z.string(),
      end: z.string(),
    }).optional(),
    filters: z.array(z.any()).optional(),
  })

  const form = useForm({
    resolver: zodResolver(FormSchema),
  })

  const [prevTimePeriod, setPrevTimePeriod] = useState('')
  const timePeriodWatch = form.watch('timePeriod')
  const customDateRangeWatch = form.watch('customDateRange')
  const timePeriodWatchMatchesQs = _.isEqual(timePeriodWatch, qs.timePeriod)
  const customDateRangeMatchesQs = timePeriodWatch === 'custom'
    ? _.isEqual(customDateRangeWatch, qs.customDateRange ? JSON.parse(qs.customDateRange as string) : {})
    : true
  // GOTCHA: Regular watch doesn't trigger useEffect on change for field arrays!
  // Memoize so the array reference stays stable across renders unless the URL
  // actually changed — otherwise downstream useMemos that include this in
  // their deps invalidate every render and trigger continuous re-renders.
  const filtersFromQs = React.useMemo(() => getFiltersFromQs(qs), [location.search])
  const filtersWatch = useWatch({ control: form.control, name: 'filters' })
  const filtersWatchMatchesQs = _.isEqual(filtersWatch, filtersFromQs)


  /**
   * Set Tab View from qs
   */
  useEffect(() => {
    const view = qs.view
    if (view) {
      dispatch(setCurrentTab(view))
    }
  }, [])

  /**
   *
   * Set default values on load based on the url state
   *
   */
  useEffect(() => {
    form.reset({
      timePeriod: qs.timePeriod || DEFAULT_TIME_PERIOD,
      customDateRange: qs.customDateRange ? JSON.parse(qs.customDateRange as string) : { start: '', end: '' },
      filters: filtersFromQs,
    })
  }, [])

  /**
   *
   * Fetch unique field values and turn them into select field options
   *
   */
  const getUniqueFieldValueOptions = useCallback(
    async (fieldName) => {
      if (fieldName === 'checkout_stage') {
        return cartStatusOptions
      }
      if (fieldName === 'domain') {
        return domains.map((domain) => ({
          label: domain,
          value: domain,
        }))
      }
      const stateKey = `${fieldName}--${timePeriodWatch}--${customDateRangeWatch?.start || ''}--${customDateRangeWatch?.end || ''}`
      const uniqueFieldValuesStatus = store.getState().trafficReport.uniqueFieldValues[stateKey]?.status
      if (uniqueFieldValuesStatus === 'succeeded') {
        const uniqueFieldValuesData = store.getState().trafficReport.uniqueFieldValues[stateKey]?.data
        return uniqueFieldValuesData
          .filter((dbResItem) => !_.isNil(dbResItem?.[fieldName]))
          .map((dbResItem) => ({
            label: dbResItem?.[fieldName],
            value: dbResItem?.[fieldName],
          }))
      }
      const uniqueFieldValuesRes = await dispatch(
        fetchUniqueFieldValues({
          fieldName,
          timePeriod: timePeriodWatch,
          timezone,
          customDateRange: customDateRangeWatch,
        })
      ).unwrap()
      return uniqueFieldValuesRes
        .filter((dbResItem) => !_.isNil(dbResItem?.[fieldName]))
        .map((dbResItem) => ({
          label: dbResItem?.[fieldName],
          value: dbResItem?.[fieldName],
        }))
    },
    [timePeriodWatch, customDateRangeWatch, domains, timezone, dispatch]
  )

  const [optionsCache, setOptionsCache] = useState<Record<string, { label: string; value: string }[]>>({})
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({})
  const optionsFetchKeyRef = useRef<Record<string, string>>({})
  const [filterVisible, setFilterVisible] = useState(false)
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < COMPACT_BREAKPOINT
  )

  useEffect(() => {
    setOptionsCache({})
    optionsFetchKeyRef.current = {}
  }, [timePeriodWatch, customDateRangeWatch?.start, customDateRangeWatch?.end])

  const onEnsureOptions = useCallback(
    async (fieldName: string) => {
      if (!fieldName) return
      // Free-text filter fields don't have a unique-values list — skip the fetch.
      if (FREE_TEXT_FILTER_FIELDS.has(fieldName)) return
      const key = `${fieldName}|${timePeriodWatch}|${customDateRangeWatch?.start || ''}|${customDateRangeWatch?.end || ''}`
      if (optionsFetchKeyRef.current[fieldName] === key) return
      setLoadingFields((prev) => ({ ...prev, [fieldName]: true }))
      try {
        const opts = await getUniqueFieldValueOptions(fieldName)
        setOptionsCache((prev) => ({ ...prev, [fieldName]: opts || [] }))
        optionsFetchKeyRef.current[fieldName] = key
      } catch {
        setOptionsCache((prev) => ({ ...prev, [fieldName]: [] }))
      } finally {
        setLoadingFields((prev) => ({ ...prev, [fieldName]: false }))
      }
    },
    [timePeriodWatch, customDateRangeWatch?.start, customDateRangeWatch?.end, getUniqueFieldValueOptions]
  )

  useEffect(() => {
    if (!filterVisible) return
    ;(filtersWatch || []).forEach((row) => {
      if (row?.field) onEnsureOptions(row.field)
    })
  }, [filterVisible, filtersWatch, onEnsureOptions])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < COMPACT_BREAKPOINT)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  /**
   *
   * Handle submit
   *
   */
  const onSubmit = (values) => {
    const _timePeriod = values.timePeriod
    const _customDateRange = values.customDateRange
    // Preserve each filter's `op` (range filters need op:'range'); fall back
    // to 'eq' for legacy form rows that don't have one set.
    const _filters = values.filters.map((f) => ({ ...f, op: f.op || 'eq' }))

    // Set the qs
    const qsStr = queryString.stringify({
      ...qs,
      timePeriod: _timePeriod,
      ...(_timePeriod === 'custom' && _customDateRange ? { customDateRange: JSON.stringify(_customDateRange) } : {}),
      ...(_filters ? { filters: JSON.stringify(_filters) } : {}),
    })
    navigate({ pathname: location.pathname, search: `?${qsStr}` })

    const currentView = qs.view

    // Reset the fetch status for the traffic report and metrics
    dispatch(resetFetchTrafficReportStatus())
    dispatch(resetFetchTrafficMetricsStatus())

    // Reset the ads tab data
    dispatch(resetFetchGoogleAdsMetricsSummaryStatus())
    dispatch(resetFetchFbAdsMetricsSummaryStatus())
    dispatch(resetFetchKlaviyoAdsMetricsSummaryStatus())
    dispatch(resetFetchPinterestAdsMetricsSummaryStatus())
    dispatch(resetFetchTiktokAdsMetricsSummaryStatus())
  }

  /**
   *
   * Fetch the traffic cart reports ON LOAD
   *
   */
  const trafficReport = useSelector(selectTrafficReport)
  const trafficReportStatus = useSelector(selectTrafficReportStatus)
  useEffect(() => {
    const fn = async () => {
      const timePeriodChanged = !_.isEqual(timePeriodWatch, prevTimePeriod)
      if (!timePeriodChanged) return

      if (trafficReportStatus === 'idle') {
        const _timePeriod = qs.timePeriod || DEFAULT_TIME_PERIOD
        const _customDateRange = qs.customDateRange ? JSON.parse(qs.customDateRange as string) : undefined
        const _trafficReport = await dispatch(
          fetchTrafficReport({
            timePeriod: _timePeriod,
            customDateRange: _customDateRange,
            filters: filtersFromQs,
          })
        )

        // NOTE: Separately save total pages data on load since it's only called once when
        // page === 1 (to prevent re-running the query)
        const _pages = _trafficReport?.payload?.pages || {}
        dispatch(setTabTotalPages(_pages))
      }
    }
    fn()
  }, [trafficReportStatus])
  // console.log('trafficReport', trafficReport)

  /**
   *
   * Handle tabs
   *
   */
  const [exportData, setExportData] = useState([])
  const dataTableTimeFormatter = (date) => {
    return date ? moment(date).format('YYYY-MM-DD (h:mma)') : ''
  }
  const productsFormatter = (products) => {
    return products?.map((product) => `●  ${product.name}`).join('\n')
  }
  const currencyFormatter = (val) => {
    if (val == null || val === '') return ''
    const n = Number(val)
    if (Number.isNaN(n)) return String(val)
    return n.toLocaleString(undefined, { style: 'currency', currency })
  }
  const usdFormatter = (val) => {
    if (val == null || val === '') return ''
    const n = Number(val)
    if (Number.isNaN(n)) return String(val)
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
  }
  const integerFormatter = (val) => {
    if (val == null || val === '') return ''
    const n = Number(val)
    if (Number.isNaN(n)) return String(val)
    return Math.round(n).toLocaleString()
  }
  const TRAFFIC_COL_FORMATTERS: Record<string, (v: any) => any> = {
    last_date: dataTableTimeFormatter,
    sales: currencyFormatter,
    sales_usd: usdFormatter,
    lineItems: productsFormatter,
    cart_value: currencyFormatter,
    cart_value_usd: usdFormatter,
    max_line_price: currencyFormatter,
    max_line_price_usd: usdFormatter,
    min_line_price: currencyFormatter,
    min_line_price_usd: usdFormatter,
    total_quantity: integerFormatter,
  }
  const TRAFFIC_COL_TITLE_OVERRIDES: Record<string, string> = {
    identifier: 'Cart ID',
    last_date: 'Last Landed On',
    country_code: 'Country Code',
  }
  const trafficconfig = (useSelector(selectRootUser) as any)?.trafficconfig

  const buildTrafficTabColumns = (tabName: string) => {
    const reportKey = tabNameToReportKey(tabName)
    if (!reportKey) return [] as Array<{ name: string; title: string; formatter?: any }>
    const allKeys = trafficSessionsConfig.keys
    const titles = trafficSessionsConfig.titles
    const userCols = trafficconfig?.columns?.[reportKey] || {}
    const defaultInvisible = parseDefaultInvisibleSet((reportDefaultInvisibleColumns as any)[reportKey])
    const sorted = resolveVisibleSortedKeys(allKeys, userCols, defaultInvisible)

    return sorted.map((k) => ({
      name: k,
      title: TRAFFIC_COL_TITLE_OVERRIDES[k] || titles[k] || k,
      formatter: TRAFFIC_COL_FORMATTERS[k],
    }))
  }

  const buildTrafficEditableThead = (tabName: string, cols: Array<{ name: string; title: string }>) => (
    <TrafficEditableThead
      tabName={tabName}
      visibleKeys={cols.map((c) => c.name)}
      names={cols.reduce((acc: Record<string, string>, c) => ({ ...acc, [c.name]: c.title }), {})}
      timePeriod={timePeriodWatch}
      customDateRange={customDateRangeWatch}
      filters={filtersFromQs}
    />
  )

  // Memoize per-tab columns + tab content so passing them to DataTableTab
  // doesn't churn references on every Traffic render. Without this, DataTable's
  // useEffect on `data` fires every render, dispatches setSelectedRows, and
  // ends up reconciling all cells continuously (visible as flickering DOM
  // nodes in dev tools and broken text selection).
  //
  // Use primitive deps for customDateRangeWatch (start/end strings) so a fresh
  // object identity from `form.watch` doesn't invalidate the memo on its own.
  const customDateStart = customDateRangeWatch?.start
  const customDateEnd = customDateRangeWatch?.end
  const dataTabs = React.useMemo(
    () =>
      (['orders', 'checkouts', 'carts', 'visitors', 'sessions'] as const).map((tabName) => {
        const tabCols = buildTrafficTabColumns(tabName)
        return {
          name: tabName,
          title: tabName.charAt(0).toUpperCase() + tabName.slice(1),
          content: (
            <DataTableTab
              tabName={tabName}
              columns={tabCols}
              trafficReport={trafficReport}
              setExportData={setExportData}
              customThead={buildTrafficEditableThead(tabName, tabCols)}
            />
          ),
        }
      }),
    [trafficconfig, trafficReport, timePeriodWatch, customDateStart, customDateEnd, filtersFromQs]
  )

  const tabs = [
    {
      name: 'metrics',
      title: 'Overview',
      content: (
        <div className="[&_.conversion-metrics-section]:!tw-mt-0">
          <MetricsTab
            // location={location}
            // history={history}
            // prevTimePeriod={prevTimePeriod}
            // timePeriodWatch={timePeriodWatch}
            setExportData={setExportData}
            trafficReport={trafficReport}
            currency={currency}
          />
        </div>
      ),
    },
    {
      name: 'ads',
      title: 'Ads',
      content: <AdsTab prevTimePeriod={prevTimePeriod} timePeriodWatch={timePeriodWatch} customDateRangeWatch={customDateRangeWatch} filtersWatch={filtersWatch} />,
    },
    {
      name: 'mmm',
      title: 'Marketing Mix',
      content: <MMMTab currency={currency} timePeriodWatch={timePeriodWatch} customDateRangeWatch={customDateRangeWatch} />,
    },
    {
      name: 'overview',
      title: 'Trends',
      content: (
        <OverviewTab
          location={location}
          history={history}
          prevTimePeriod={prevTimePeriod}
          timePeriodWatch={timePeriodWatch}
          customDateRangeWatch={customDateRangeWatch}
          filtersWatch={filtersWatch}
          currency={currency}
        />
      ),
    },
    ...dataTabs,
  ]

  const currentTab = useSelector(selectCurrentTab)
  const tabPagination = useSelector((state) => selectTabPagination(state, currentTab))
  // console.log('currentTab', currentTab)
  // console.log('tabPagination', tabPagination)

  /**
   *
   * Handle form for mobile view
   *
   */
  // const mobileTabForm = useForm({
  //   defaultValues: {
  //     mobileTab: 'overview',
  //   },
  // })
  const MobileTabFormSchema = z.object({
    mobileTab: z.string({
      required_error: 'Please select a tab.',
    }),
  })

  const mobileTabForm = useForm({
    resolver: zodResolver(MobileTabFormSchema),
  })
  const mobileTabWatch = mobileTabForm?.watch('mobileTab')

  // Set default for mobile tab form
  useEffect(() => {
    const view = qs.view
    if (view) {
      mobileTabForm.reset({
        mobileTab: view,
      })
    } else {
      mobileTabForm.reset({
        mobileTab: 'metrics',
      })
    }
  }, [])

  /**
   *
   * Handle export
   *
   */
  const tableColumns = useSelector(selectMetricTableColumns)
  const tableRows = useSelector(selectMetricTableRows)

  const handleExport = () => {
    const today = moment().format('YYYY_MM_DD')

    if (currentTab === 'overview') {
      jsonToCsvExport({
        filename: `traffic_${currentTab}_${today}`,
        data: tableRows.map((row) => _.omit(row, ['color'])),
        headers: tableColumns.map((col) => col.accessorKey),
      })
    } else if (currentTab === 'metrics') {
      exportData.forEach((section) => {
        jsonToCsvExport({
          filename: `traffic_${currentTab}_${section.sectionName}_${today}`,
          data: section.data,
          headers: section.header,
        })
      })
    } else if (currentTab === 'orders') {
      jsonToCsvExport({
        filename: `traffic_${currentTab}_${today}`,
        data: exportData[0].data,
        headers: exportData[0].headers,
      })
    } else if (currentTab === 'checkouts') {
      jsonToCsvExport({
        filename: `traffic_${currentTab}_${today}`,
        data: exportData[0].data,
        headers: exportData[0].headers,
      })
    } else if (currentTab === 'carts') {
      jsonToCsvExport({
        filename: `traffic_${currentTab}_${today}`,
        data: exportData[0].data,
        headers: exportData[0].headers,
      })
    } else if (currentTab === 'visitors') {
      jsonToCsvExport({
        filename: `traffic_${currentTab}_${today}`,
        data: exportData[0].data,
        headers: exportData[0].headers,
      })
    } else if (currentTab === 'sessions') {
      jsonToCsvExport({
        filename: `traffic_${currentTab}_${today}`,
        data: exportData[0].data,
        headers: exportData[0].headers,
      })
    }
  }

  const settingsTabEnabled = ['metrics', 'ads'].includes(currentTab)

  if (!(addOnEnabled || addOnUpgradeDismissed || isAdmin || isTrialing)) {
    return <TrafficUpgradeModal />
  }

  const applyDisabled =
    timePeriodWatchMatchesQs && customDateRangeMatchesQs && filtersWatchMatchesQs

  const filterSideMenuProps = {
    form,
    filters: filtersWatch || [],
    propertyOptions,
    optionsCache,
    loadingFields,
    onEnsureOptions,
    onApply: () => form.handleSubmit(onSubmit)(),
    applyDisabled,
  }

  return (
    <>
      <div
        id="traffic-body"
        className={`tw-min-w-0 tw-max-w-full ${filterVisible && !isMobile ? 'filter-sidebar-open' : ''}`}
        style={{ position: 'relative' }}
      >
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-gap-y-3 tw-mb-4">
          <div className="tw-flex tw-items-center tw-min-w-0">
            <TrafficDateFilter
              timePeriod={timePeriodWatch}
              customDateRange={customDateRangeWatch}
              disabled={trafficReportStatus === 'loading'}
              onChange={({ timePeriod: tp, customDateRange: cdr }) => {
                setPrevTimePeriod(form.getValues('timePeriod'))
                form.setValue('timePeriod', tp)
                if (cdr) {
                  form.setValue('customDateRange', cdr)
                } else if (tp !== 'custom') {
                  form.setValue('customDateRange', { start: '', end: '' })
                }
                if (tp !== 'custom') {
                  dispatch(
                    fetchTrafficReport({
                      timePeriod: tp,
                      customDateRange: form.getValues('customDateRange'),
                      pagination: tabPagination,
                      tabName: currentTab,
                      filters: filtersFromQs,
                    })
                  )
                }
              }}
            />
            <Button
              type="button"
              disabled={applyDisabled}
              className="!tw-h-9 tw-ml-2 !tw-bg-[#0d6efd] hover:!tw-bg-[#0b5ed7] !tw-text-white tw-border-0 tw-shadow-sm disabled:!tw-opacity-50 disabled:hover:!tw-bg-[#0d6efd]"
              onClick={() => form.handleSubmit(onSubmit)()}
            >
              Apply
            </Button>
          </div>
          <div className="tw-flex-1 tw-min-w-[1rem]" aria-hidden />
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-shrink-0">
            {settingsTabEnabled ? <SettingsModal tab={currentTab} /> : null}
            <Button
              className="!tw-px-3 !tw-h-9 tw-text-[1rem] !tw-bg-[#0d6efd] hover:!tw-bg-[#0b5ed7] !tw-text-white tw-border-0 tw-shadow-sm [&_svg]:tw-text-white"
              onClick={handleExport}
            >
              <IoIosSave />
              <div className="tw-ml-1.5 tw-text-sm">Export</div>
            </Button>
            <Button
              type="button"
              className="!tw-h-9 !tw-w-9 !tw-p-0 !tw-bg-[#0d6efd] hover:!tw-bg-[#0b5ed7] tw-border-0 tw-shadow-sm tw-flex tw-items-center tw-justify-center"
              onClick={() => setFilterVisible(true)}
              aria-label="Open filters"
              title="Filters"
            >
              <BsFunnelFill className="tw-h-[18px] tw-w-[18px] tw-text-white" aria-hidden />
            </Button>
          </div>
        </div>

        {filterVisible && isMobile ? (
          <Sheet open={filterVisible} onOpenChange={(open) => !open && setFilterVisible(false)}>
            <SheetContent
              side="right"
              className="tw-w-full tw-max-w-full tw-h-full tw-min-h-screen tw-top-0 tw-right-0 tw-p-0 tw-flex tw-flex-col tw-gap-0 data-[state=open]:tw-duration-200 data-[state=closed]:tw-duration-150"
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-flex-shrink-0">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Filter className="tw-w-4 tw-h-4 tw-text-slate-600" />
                  <span className="tw-font-semibold tw-text-slate-900">Filters</span>
                </div>
              </div>
              <div className="filter-sidebar-content tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-p-4 tw-overflow-hidden">
                <TrafficFilterSideMenu {...filterSideMenuProps} />
              </div>
            </SheetContent>
          </Sheet>
        ) : filterVisible && !isMobile ? (
          <div className="tw-fixed tw-right-0 tw-top-[48px] tw-w-80 tw-h-[calc(100vh-48px)] tw-bg-white tw-border-l tw-border-slate-200 tw-shadow-lg tw-z-40 tw-flex tw-flex-col">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setFilterVisible(false)
              }}
              className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-flex-shrink-0 tw-border-0 tw-cursor-pointer hover:tw-bg-slate-100 tw-transition-colors tw-text-left"
              title="Hide filters"
              aria-label="Hide filters"
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <Filter className="tw-w-4 tw-h-4 tw-text-slate-600" />
                <span className="tw-font-semibold tw-text-slate-900">Filters</span>
              </div>
              <ChevronRight className="tw-w-4 tw-h-4 tw-text-slate-500 tw-flex-shrink-0" />
            </button>
            <div className="filter-sidebar-content tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-overflow-x-hidden tw-overflow-y-hidden tw-p-4">
              <TrafficFilterSideMenu {...filterSideMenuProps} />
            </div>
          </div>
        ) : null}

        {!filterVisible && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setFilterVisible(true)
            }}
            className="tw-fixed tw-right-0 tw-top-1/2 tw--translate-y-1/2 tw-z-40 tw-bg-white tw-border tw-border-slate-200 tw-border-r-0 tw-rounded-l-lg tw-shadow-lg tw-p-3 tw-cursor-pointer hover:tw-bg-slate-50 tw-transition-colors"
            title="Open filters"
            aria-label="Open filters"
          >
            <ChevronLeft className="tw-w-5 tw-h-5 tw-text-slate-600" />
          </button>
        )}

        <div className="tw-w-full">
          <div className="tw-hidden md:tw-block tw-w-full">
            <UnderlineTabs
                value={currentTab}
                onValueChange={(value) => {
                  const searchParams = new URLSearchParams(window.location.search)
                  searchParams.set('view', value)
                  navigate({ search: searchParams.toString() })
                  dispatch(setCurrentTab(value))
                }}
                className="tw-w-full"
              >
                <UnderlineTabsList className="tw-w-full tw-rounded-none">
                  {tabs?.map(({ name, title }, i) => (
                    <UnderlineTabsTrigger key={i} value={name}>
                      {title}
                    </UnderlineTabsTrigger>
                  ))}
                </UnderlineTabsList>

                {tabs?.map(({ name, content }, i) => (
                  <UnderlineTabsContent key={i} value={name} className="tw-mt-4">
                    {trafficReportStatus === 'loading' ? (
                      <LoadingState />
                    ) : (
                      <div className={filterVisible && !isMobile ? 'tw-pr-2' : ''}>{content}</div>
                    )}
                  </UnderlineTabsContent>
                ))}
              </UnderlineTabs>
            </div>

            <div className="tw-block md:tw-hidden">
              <Form {...mobileTabForm}>
                <Combobox
                  form={mobileTabForm}
                  name="mobileTab"
                  label="Tab"
                  labelClassName="tw-text-sm !tw-font-bold"
                  placeholder="Select a tab"
                  options={tabs?.map(({ name, title, content }) => ({
                    label: title,
                    value: name,
                  }))}
                  onSelectOverride={(selected) => {
                    const searchParams = new URLSearchParams(window.location.search)
                    searchParams.set('view', selected)
                    navigate({ search: searchParams.toString() })
                    mobileTabForm.setValue('mobileTab', selected)
                    dispatch(setCurrentTab(selected))
                  }}
                />
              </Form>

              {mobileTabWatch && (
                <div className="tw-bg-white tw-rounded-md tw-px-4 tw-py-4 tw-shadow tw-mt-4">
                  {trafficReportStatus === 'loading' ? (
                    <LoadingState />
                  ) : (
                    <div className="[&_.conversion-metrics-section]:!tw-mt-0">
                      {tabs?.find(({ name }) => name === mobileTabWatch)?.content}
                    </div>
                  )}
                </div>
              )}
            </div>
        </div>
      </div>
    </>
  )
}

export default Traffic

export const EmptyState = () => {
  return (
    <div className="tw-bg-white tw-rounded-md tw-p-4 tw-shadow">
      <div className="tw-h-[368px] tw-bg-slate-100 tw-flex tw-items-center tw-justify-center tw-text-slate-500">
        No data to show
      </div>
    </div>
  )
}

export const LoadingState = () => {
  return (
    <div className="tw-bg-white tw-rounded-md tw-p-4 tw-shadow">
      <div className="tw-h-[368px] tw-bg-slate-100 tw-flex tw-items-center tw-justify-center tw-text-slate-500">
        <div className="tw-flex tw-items-center tw-justify-center tw-space-x-1 tw-min-h-80">
          <div className="tw-animate-spin">
            <LoaderCircle />
          </div>
          <div>Loading data...</div>
        </div>
      </div>
    </div>
  )
}

